import LinkifyIt from 'linkify-it'
import tlds from 'tlds'

/**
 * URL type classification for external link warnings
 */
export type UrlType = 'official' | 'whitelisted' | 'external' | 'blocked'

/**
 * Official PixAI domains that don't require warnings
 */
const OFFICIAL_URLS: RegExp[] = [
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*pixai\.art(\/.*)?$/,
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*pix\.ai(\/.*)?$/,
  // X (Twitter)
  /^(https?:\/\/)?x\.com\/@?PixAI_Official(\/.*)?$/,
  /^(https?:\/\/)?twitter\.com\/@?PixAI_Official(\/.*)?$/,
  // Instagram
  /^(https?:\/\/)?(www\.)?instagram\.com\/pixai_official(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?instagram\.com\/pixai\.official(\/.*)?$/,
  // Facebook
  /^(https?:\/\/)?(www\.)?facebook\.com\/profile\.php\?id=100081244069311/,
  // YouTube
  /^(https?:\/\/)?(www\.)?youtube\.com\/@PixaiART(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?youtube\.com\/@PixAIJP(\/.*)?$/,
  // TikTok
  /^(https?:\/\/)?(www\.)?tiktok\.com\/@pixai\.art\.official(\/.*)?$/,
  // Naver Cafe
  /^(https?:\/\/)?cafe\.naver\.com\/pixaikr(\/.*)?$/,
  // Discord - Exact match for invite links
  /^(https?:\/\/)?discord\.com\/invite\/pixai\/?$/,
  /^(https?:\/\/)?discord\.gg\/pixai\/?$/,
]
/**
 * Whitelisted domains that are considered safe but not official
 */
const WHITELISTED_URLS: RegExp[] = [
  /^(https?:\/\/)?(www\.)?twitter\.com(\/.*)?$/,
  /^(https?:\/\/)?x\.com(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?instagram\.com(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?facebook\.com(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?youtube\.com(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?tiktok\.com(\/.*)?$/,
  /^(https?:\/\/)?cafe\.naver\.com(\/.*)?$/,
  /^(https?:\/\/)?(www\.)?discord\.com(\/.*)?$/,
  /^(https?:\/\/)?discord\.gg(\/.*)?$/,
  /^(https?:\/\/)?civitai\.com(\/.*)?$/,
]

/**
 * Check if a URL is official, whitelisted, or external
 * @param url - The URL string to check
 * @returns The type of URL: 'official', 'whitelisted', or 'external'
 */
export function checkUrlType(url: string): UrlType {
  // Check for official urls
  for (const u of OFFICIAL_URLS) {
    if (u.test(url)) {
      return 'official'
    }
  }

  // Check for whitelisted urls
  for (const u of WHITELISTED_URLS) {
    if (u.test(url)) {
      return 'whitelisted'
    }
  }

  // If not official or whitelisted, it's external
  return 'external'
}

export function getSafeLinkUrl(url: string, source?: string): string {
  let safeUrl = `/external-link?url=${encodeURIComponent(url)}`

  if (source) {
    safeUrl = safeUrl + `&source=${encodeURIComponent(source)}`
  }

  return safeUrl
}

const linkify = new LinkifyIt()
linkify.tlds(tlds)

/**
 * Normalize linkify-it match URL to prefer https for scheme-less matches.
 * linkify-it defaults to http:// for bare domains, but we prefer https://.
 */
function normalizeMatchUrl(match: {
  schema: string
  raw: string
  url: string
}): string {
  // schema is empty for fuzzy (scheme-less) matches like "example.com"
  if (!match.schema) {
    return `https://${match.raw}`
  }
  return match.url
}

export interface ParsedSegment {
  type: 'text' | 'url'
  content: string
  href?: string
}

/**
 * Detects if a string is a URL and returns the href to use
 * @param text - The text to check
 * @returns The href if it's a URL, undefined otherwise
 */
export function detectUrl(text: string): string | undefined {
  const trimmed = text.trim()
  const matches = linkify.match(trimmed)

  // Check if the entire text is a single URL
  if (
    matches &&
    matches.length === 1 &&
    matches[0].index === 0 &&
    matches[0].lastIndex === trimmed.length
  ) {
    return normalizeMatchUrl(matches[0])
  }

  return undefined
}

/**
 * Parses text content and identifies URL segments
 * @param content - The text content to parse
 * @returns Array of parsed segments
 */
export function parseURLFromTextContent(content: string): ParsedSegment[] {
  const matches = linkify.match(content)

  if (!matches || matches.length === 0) {
    return [{ type: 'text', content }]
  }

  const segments: ParsedSegment[] = []
  let lastIndex = 0

  for (const match of matches) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.substring(lastIndex, match.index),
      })
    }

    segments.push({
      type: 'url',
      content: match.raw,
      href: normalizeMatchUrl(match),
    })

    lastIndex = match.lastIndex
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.substring(lastIndex),
    })
  }

  return segments
}
